import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CirclePause from '@gravity-ui/icons/CirclePause';
import Ellipsis from '@gravity-ui/icons/Ellipsis';
import TrashBin from '@gravity-ui/icons/TrashBin';
import { Button, DropdownMenu, Icon, Label, Loader, TextArea } from '@gravity-ui/uikit';
import { lengthConfig } from '@paskills/validation';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { ApiError, deleteApp, forceStopApp, requestCancelInstallableAppModeration, stopApp } from 'client/api';
import { IconMedium } from 'client/blocks/AppIcons/AppIcons';
import { ModalWithConfirmation } from 'client/blocks/ModalWithConfirmation/ModalWithConfirmation';
import { OperationStatus } from 'client/blocks/statuses/OperationStatus/OperationStatus';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { FormErrors } from 'client/hocs/types';
import { useSkillIsDraft } from 'client/hooks/useIsDraft';
import { routes } from 'client/lib/routes';
import { DraftStatus } from 'client/model/draft';
import { Channel, InstallableApp } from 'client/model/skill';
import { SmartHomeFormSettings } from 'client/types';
import { getCanEditApp, getCanEditAsAdmin } from 'client/utils';
import { convertToErrors, hasFieldErrors, mapApiErrorsToFormErrors } from 'client/utils/errorMessage';
import * as keyset from 'i18n/DraftControlPanel';
import * as errorsKeyset from 'i18n/SettingsErrors';
import * as settingsLabels from 'i18n/SettingsLabels';

import styles from './ControlPanel.module.scss';

type TModal = 'cancelReview' | 'changeToDraft' | 'deleteApp' | 'stopApp' | 'forceStopApp';

const t = i18n(keyset);
const tError = i18n(errorsKeyset);
const tSettings = i18n(settingsLabels);

interface Props {
    isSubmitting?: boolean;
    setErrors: (errors: FormErrors<SmartHomeFormSettings & { network: string }>, cb?: () => void) => void;
    isNonValidationError: boolean;
    settingValues: SmartHomeFormSettings;
    settingFormErrors: FormErrors<SmartHomeFormSettings & { esia?: string }>;
    appName: string;
    logoUrl: string | null | undefined;
}

// TODO: move to smart home specific fields
const convertTypedErrorToMessage = (key: string, value: string) => {
    const typedKey = key as keyof FormErrors<SmartHomeFormSettings & { network?: string; esia?: string }>;
    if (
        typedKey === 'network' &&
        value === 'Name/ActivationPhrases validation error: &apos;Содержит общие фразы&apos;'
    ) {
        return `${tSettings('activation-phrases')}: ${value}`;
    }
    if (typedKey === 'network') {
        return `${tError('network')}: ${value}`;
    }
    if (typedKey === 'esia') {
        return `${tError('need-esia-profile-title')}: ${value}`;
    }
    if (typedKey === 'supportedLanguages') {
        return `${tSettings('language')}: ${tError('validation-error')}`;
    }
    return String(value);
};

export const SmartHomeControlPanel: React.FC<Props> = ({
    appName,
    isSubmitting,
    setErrors,
    isNonValidationError,
    settingValues,
    settingFormErrors,
    logoUrl,
}) => {
    const [forceStopComment, setForceStopComment] = useState<string>();
    const [openModalType, setOpenModalType] = useState<TModal | undefined>();
    const [isLoading, setIsLoading] = useState(false);

    const { aliceApp, refreshAliceApp, operations } = useContext(CurrentAliceAppContext)!;

    const { isDraft, setIsDraft } = useSkillIsDraft(aliceApp.id);
    const navigate = useNavigate();
    const canEditAsAdmin = getCanEditAsAdmin();
    const disabled = !getCanEditApp(aliceApp);

    useEffect(() => {
        setForceStopComment(undefined);
    }, [aliceApp.id]);

    const formErrors = convertToErrors(settingFormErrors, convertTypedErrorToMessage);
    const hasValidationErrors = hasFieldErrors(settingFormErrors);

    if (isNonValidationError) {
        formErrors.push(tError('unknown-error'));
    }

    const wrapActionCall = useCallback(
        (callback: (appId: string, appChannel: Channel, comment?: string) => Promise<void | InstallableApp>) => {
            return async () => {
                setIsLoading(true);
                try {
                    await callback(aliceApp.id, aliceApp.channel);
                    refreshAliceApp();
                    setOpenModalType(undefined);
                } catch (error) {
                    if (error instanceof ApiError) {
                        setErrors(mapApiErrorsToFormErrors(error.message));
                    } else {
                        let errorMessage = tError('network');
                        if (
                            !!error &&
                            typeof error === 'object' &&
                            'message' in error &&
                            typeof error.message === 'string'
                        ) {
                            errorMessage = error.message;
                        }
                        setErrors(mapApiErrorsToFormErrors(errorMessage));
                        setOpenModalType(undefined);
                    }
                }
                setIsLoading(false);
            };
        },
        [aliceApp.id, aliceApp.channel, refreshAliceApp, setErrors],
    );

    const cancelReview = wrapActionCall(requestCancelInstallableAppModeration);
    const stopInstallableApp = wrapActionCall(stopApp);

    const deleteInstallableApp = useCallback(async () => {
        setIsLoading(true);
        try {
            await deleteApp(aliceApp.id, aliceApp.channel);
            setTimeout(async () => {
                navigate(routes.app.installableApps(aliceApp.channel));
                refreshAliceApp();
            }, 3000);
        } catch (error) {
            let errorMessage = tError('network');
            if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
                errorMessage = error.message;
            }
            setErrors(mapApiErrorsToFormErrors(errorMessage));
        }
        setIsLoading(false);
        setOpenModalType(undefined);
    }, [aliceApp.id, aliceApp.channel, refreshAliceApp, setErrors, navigate]);

    const forceStopInstallableApp = useCallback(
        async (comment?: string) => {
            if (!comment) return;
            setIsLoading(true);
            try {
                await forceStopApp(aliceApp.id, aliceApp.channel, { comment });
                refreshAliceApp();
            } catch (error) {
                let errorMessage = tError('network');
                if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
                    errorMessage = error.message;
                }
                setErrors(mapApiErrorsToFormErrors(errorMessage));
            }
            setIsLoading(false);
            setOpenModalType(undefined);
        },
        [aliceApp.id, aliceApp.channel, refreshAliceApp, setErrors],
    );

    if (!operations.length) {
        return (
            <div className={styles.buttonsContainer}>
                <Loader />
            </div>
        );
    }

    const disableStopButton = false;

    let hideSaveButton = true;
    let hideStopButton = false;
    let hideChangeButton = false;

    if (isDraft) {
        hideSaveButton = aliceApp.draft.status !== DraftStatus['InDevelopment'];
        hideStopButton = true;
        hideChangeButton = aliceApp.draft.status === DraftStatus['InDevelopment'];
    }

    if (disabled) {
        hideSaveButton = true;
        hideStopButton = true;
        hideChangeButton = true;
    }

    let modal = null;

    if (openModalType === 'cancelReview') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('change')}
                title={t('changing-data-will-stop-moderation')}
                text={t('if-you-want-to-change-the-data')}
                onConfirm={cancelReview}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'changeToDraft') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('go-to-draft')}
                title={t('change-in-copy')}
                text={t('you-can-not-change-published-version')}
                onConfirm={() => setIsDraft(true)}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'deleteApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('delete')}
                text={t('delete-text')}
                title={t('delete-title')}
                onConfirm={deleteInstallableApp}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'stopApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('stop')}
                text={t('stop-text')}
                title={t('stop-title')}
                onConfirm={stopInstallableApp}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'forceStopApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('stop')}
                title={t('stop-title')}
                text={t('stop-text')}
                onConfirm={() => forceStopInstallableApp(forceStopComment)}
                loading={isLoading}
                confirmDisabled={!forceStopComment}
            >
                <TextArea
                    className={styles.moderatorCommentTextfield}
                    error={settingFormErrors.noteForModerator}
                    note={
                        <span className={styles.grayNote}>
                            {lengthConfig.noteForModerator - (settingValues.noteForModerator?.length || 0)}
                        </span>
                    }
                    onChange={(e) => setForceStopComment(e.target.value)}
                    placeholder={t('force-stop-not-empty-comment')}
                    rows={5}
                    size="xl"
                    value={forceStopComment}
                    errorMessage={!forceStopComment && t('force-stop-not-empty-comment')}
                />
            </ModalWithConfirmation>
        );
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.appTitle}>
                    <IconMedium imageUrl={logoUrl} />
                    <h1 className={styles.appTitle__header}>{appName}</h1>
                    <div className={styles.appTitle__mobileStatusIcon}>
                        <OperationStatus isDraft={isDraft} />
                    </div>
                </div>
                <div className={styles.smartHomeButtonsContainer}>
                    <Button
                        className={clx(hideSaveButton && styles.button_hidden)}
                        disabled={hasValidationErrors}
                        loading={isSubmitting || isLoading}
                        type="submit"
                    >
                        {t('save')}
                    </Button>
                    <Button
                        className={clx(hideStopButton && styles.button_hidden)}
                        disabled={disableStopButton}
                        onClick={() => setOpenModalType(canEditAsAdmin ? 'forceStopApp' : 'stopApp')}
                        loading={isLoading}
                    >
                        <Icon data={CirclePause} />
                        {t('stop')}
                    </Button>
                    <Button
                        className={clx(hideChangeButton && styles.button_hidden)}
                        onClick={() => setOpenModalType(isDraft ? 'cancelReview' : 'changeToDraft')}
                        loading={isLoading}
                    >
                        {t('change')}
                    </Button>
                    {!disabled && (
                        <DropdownMenu
                            renderSwitcher={(props) => (
                                <Button {...props}>
                                    <Icon size={16} data={Ellipsis} />
                                </Button>
                            )}
                            items={[
                                {
                                    action: () => setOpenModalType('deleteApp'),
                                    iconStart: <Icon size={16} data={TrashBin} />,
                                    text: t('delete'),
                                    theme: 'danger',
                                },
                            ]}
                        />
                    )}
                </div>
            </div>
            <div className={clx(styles.errorsBlock, formErrors.length === 0 && styles.errorsBlock_hidden)}>
                {formErrors.map((errorMessage) => (
                    <Label key={errorMessage} className={styles.errorLabel} theme="danger">
                        {errorMessage}
                    </Label>
                ))}
            </div>
            {modal}
        </>
    );
};
